import * as React from 'react';
import { InfoSetById } from '@/clients/queries/contentful/InfoSetById.graphql';
import Button, { variantFromString } from 'components/src/button';
import Link from 'next/link';
import TextWithInlineColor from './TextWithInlineColor';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Paragraph } from 'components/src/typography';
import { clsx } from 'clsx';
import ISKFormat from './common/ISKFormat';
import { useQuery } from '@apollo/client';
import { InfoSetByIdQuery, InfoSetByIdQueryVariables } from 'types/generated';
import { ImageWithFocalPoint } from './ImageWithFocalPoint';
import { formatLink } from '@/utils/utils';
import { Log } from '@/logging/datadog';

type Props = {
  infoSetId: string;
  classes?: string;
  descriptionClasses?: string;
};

const RichTextContainer = ({ data, classes }: { data: Document; classes: string }) => {
  return documentToReactComponents(data, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => {
        return <Paragraph classes={'empty:h-[18px] ' + classes}>{children}</Paragraph>;
      },
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <Link
            href={formatLink(node.data.uri)}
            className="text-md font-normal leading-[26px] text-red-100 hover:underline hover:decoration-red-100"
          >
            {children}
          </Link>
        );
      },
    },
  });
};

const InfoSetSection = ({ infoSetId, classes, descriptionClasses = 'body-lg' }: Props) => {
  const { loading, error, data } = useQuery<InfoSetByIdQuery, InfoSetByIdQueryVariables>(InfoSetById, {
    variables: { id: infoSetId },
  });

  if (error) {
    Log.error('Error in InfoSetSection', error);
    return (
      <div
        className={clsx(
          'flex max-w-[1424px] flex-col items-center justify-center justify-items-center gap-lg self-stretch rounded-28 px-xl py-xbig text-center md:col-span-10',
          classes
        )}
      />
    );
  }

  const infoSet = data?.infoSet;

  return (
    <div
      className={clsx(
        'flex max-w-[1424px] flex-col items-center justify-center justify-items-center gap-lg self-stretch rounded-28 px-xl py-xbig text-center md:col-span-10',
        classes,
        { 'min-h-[100px]': loading }
      )}
    >
      {infoSet?.overline && <p className="body-lg text-black-50">{infoSet.overline}</p>}
      {infoSet?.heading?.text && (
        <TextWithInlineColor
          classes={`display-lg`}
          heading={infoSet.heading.text.json}
          color={infoSet.heading.color}
          textColor={infoSet.heading.textColor}
          headingVariant={'small'}
        />
      )}
      <div
        className={`mx-24 items-center justify-center self-stretch text-center ${
          infoSet?.heading?.textColor ? `text-${infoSet?.heading?.textColor}` : 'text-black-50'
        }`}
      >
        <RichTextContainer data={infoSet?.descriptionMarkdown?.json} classes={descriptionClasses} />
      </div>
      {infoSet?.imagesCollection?.items[0]?.id && (
        <div className={'col-span-full grid grid-cols-5 gap-32 md:grid-cols-7 lg:grid-cols-8'}>
          {infoSet?.imagesCollection.items.map(
            (item) =>
              item && (
                <div key={item.id} className="relative col-span-1 h-56">
                  <ImageWithFocalPoint image={item} sectionId={item.id} sizes={{ default: '200px' }} />
                </div>
              )
          )}
        </div>
      )}
      {infoSet?.price && (
        <div className="items-center justify-center self-stretch text-center">
          <p className="headline-lg">
            {ISKFormat({ value: infoSet.price, showSuffix: true })}{' '}
            {infoSet.priceCondition && <span className="label-lg">{infoSet.priceCondition}</span>}
          </p>
        </div>
      )}
      {infoSet?.button?.label && infoSet.button.uri && (
        <Link href={infoSet.button?.uri}>
          <Button
            label={infoSet.button.label}
            variant={variantFromString(infoSet.button.variant)}
            classes="mx-auto"
            labelClasses="font-medium"
          />
        </Link>
      )}
    </div>
  );
};
export default InfoSetSection;
